export const PHONE_DISPLAY = "020 3873 6816";
export const PHONE_LINK = "tel:+442038736816";
export const EMAIL = "info@goldencomfortpropertymanagement.co.uk";
export const ADDRESS = "301 Cricklewood Broadway, London, NW2 6PG";

export const content = {
  en: {
    metaTitle: "Golden Comfort Property Management | Guaranteed Rent for London Landlords",
    langSwitchLabel: "العربية",
    langSwitchPath: "/ar",
    nav: [
      { label: "Why Us", href: "#why-us", id: "nav-why-us" },
      { label: "How It Works", href: "#how-it-works", id: "nav-how-it-works" },
      { label: "About", href: "#about", id: "nav-about" },
      { label: "FAQ", href: "#faq", id: "nav-faq" },
    ],
    getInTouch: "Get in Touch",
    hero: {
      overline: "Guaranteed Rent · Fully Managed · London",
      headline: "Dependable rental income, without lifting a finger.",
      sub: "We guarantee your rent for up to five years and manage everything — tenants, maintenance and every detail in between — so your property works for you, not the other way round.",
      reassurance: "Fixed rent, paid on time every month — whatever the market does.",
      positioning: "A trusted, established property operator, serving landlords across London",
      bgAlt: "London skyline at dusk",
    },
    trust: [
      "Rent guaranteed for up to five years",
      "No void periods — ever",
      "No setup or management fees",
    ],
    value: {
      overline: "Why landlords choose us",
      heading: "A simpler, safer way to let your property",
      intro: "Every part of our service is built around three principles: reducing your risk, keeping your income consistent, and making the whole experience simple.",
      imageAlt: "Professionally furnished London interior",
      items: [
        {
          title: "Guaranteed rent for up to five years",
          copy: "A fixed monthly payment, agreed at the outset and paid on time for the full term. No void periods, no arrears, no chasing — your income stays consistent whatever the market does.",
        },
        {
          title: "Complimentary refurbishment & furnishing",
          copy: "Where needed, we refresh and furnish your property at our cost — increasing tenant appeal and protecting the long-term condition of your asset, at no charge to you.",
        },
        {
          title: "Complete tenant & maintenance management",
          copy: "Rent collection, tenant liaison, inspections and day-to-day maintenance are all handled by our team. One point of contact, zero landlord involvement.",
        },
        {
          title: "Optional purchase pathways",
          copy: "At the end of your agreement, choose what suits your goals: renew, take the property back with vacant possession, or explore an agreed purchase of your property.",
        },
        {
          title: "Local London expertise",
          copy: "We source high-quality professional tenants through established London employers and networks — reliable occupants, carefully referenced and verified.",
        },
      ],
    },
    process: {
      overline: "How it works",
      heading: "From first enquiry to fully managed income",
      intro: "A clear, six-step journey designed around certainty. You will always know what happens next, who is responsible, and when your rent is paid.",
      imageAlt: "Traditional London townhouse",
      steps: [
        {
          title: "Initial consultation & property review",
          copy: "We visit your property, review its condition and rental position, and answer your questions in plain, straightforward terms.",
        },
        {
          title: "Agreement structure & guaranteed rent setup",
          copy: "We agree a fixed monthly rent and a term of up to five years, set out clearly in a simple, transparent management agreement.",
        },
        {
          title: "Refurbishment & furnishing preparation",
          copy: "Where required, we complete cosmetic improvements and furnishing at our cost, preparing your property to a professional standard.",
        },
        {
          title: "Professional tenant placement",
          copy: "We place referenced, verified professional tenants, sourced through our established London employer and relocation networks.",
        },
        {
          title: "Ongoing management, maintenance & reporting",
          copy: "We handle rent collection, maintenance, inspections and tenant liaison — keeping you informed with clear, regular updates.",
        },
        {
          title: "End-of-term options",
          copy: "As the term ends, the choice is yours: renew the agreement, take the property back with vacant possession, or discuss an agreed purchase.",
        },
      ],
    },
    authority: {
      overline: "Expertise you can rely on",
      heading: "Local knowledge. Proven judgement.",
      intro: "Golden Comfort Property Management is built on years of hands-on experience at the heart of the London property market — from prime Central London sales with two of the capital's most established estate agencies to advising international investors on off-plan developments. We apply that understanding to protect your income, your property and your peace of mind.",
      photoAlt: "Hydar Alhakim, Founder of Golden Comfort Property Management",
      founderName: "Hydar Alhakim — Founder",
      founderStory: "Hydar Alhakim has built his career at the heart of London's property market, developing a sharp understanding of what drives value, opportunity and lasting relationships. With experience across prime residential sales, new-build developments and international investment, he has helped guide millions of pounds in property transactions. Today, he brings that commercial insight, confident negotiation and client-first approach into his own property journey, combining local knowledge with an international outlook to create trusted, well-managed property solutions across London.",
      careerLine: "Career built at Kinleigh Folkard & Hayward, Dexters and GFS Developments — spanning prime Central London sales and international off-plan investment.",
      credentials: [
        "Prime Central London sales",
        "Off-plan & new-build investment",
        "Investor relations — English & Arabic",
      ],
      stats: [
        { value: "£18m+", label: "Property transactions guided across residential & off-plan" },
        { value: "£6.8m", label: "Sales exchanges achieved in a single year in prime London" },
        { value: "30+", label: "Prime Central London listings managed at any one time" },
        { value: "50+", label: "International investors advised across the UK, UAE & Saudi Arabia" },
      ],
    },
    faq: {
      overline: "Frequently asked questions",
      heading: "Clear answers, before you commit",
      items: [
        {
          q: "How does guaranteed rent work?",
          a: "We agree on a fixed monthly rental payment with you at the start of the contract and pay that amount consistently for the duration of the agreement. Your income does not fluctuate based on tenant occupancy, giving you predictable and reliable returns.",
        },
        {
          q: "What happens if the property is vacant?",
          a: "You continue to receive your agreed rent in full. Void periods are our responsibility, not yours. Our business model is built around professional tenant sourcing and strong occupancy management.",
        },
        {
          q: "Who covers maintenance costs?",
          a: "Day-to-day maintenance and tenant-related issues are managed and coordinated by our team. We handle communication, contractor management and oversight. Major structural works remain the landlord's responsibility unless otherwise agreed in writing.",
        },
        {
          q: "How are tenants sourced and vetted?",
          a: "We work with established local employers and professional networks to source high-quality tenants. All occupants are referenced, verified and placed in line with compliance requirements and best practice standards.",
        },
        {
          q: "What happens at the end of the agreement?",
          a: "You have clear options. You may renew the agreement, take the property back with vacant possession, or explore an agreed purchase pathway if that aligns with your long-term goals.",
        },
        {
          q: "Can I sell during the contract?",
          a: "Yes. We can support a sale subject to the terms of the management agreement. In some cases, we may also offer a direct purchase solution if that suits your objectives.",
        },
        {
          q: "Are there any upfront fees?",
          a: "No. We do not charge setup or management fees to place your property into our guaranteed rent programme. Our model is designed to be straightforward and aligned with a long-term partnership.",
        },
      ],
    },
    enquiry: {
      overline: "Request a call back",
      heading: "Tell us about your property",
      intro: "Share a few details and we will call you back to discuss a guaranteed rent proposal for your property. No obligation, no fees — just a clear figure and a straightforward conversation.",
      points: [
        "A guaranteed rent figure for your property",
        "A clear explanation of how the agreement works",
        "No obligation and no fees, ever",
      ],
      fields: {
        name: "Full name",
        phone: "Phone number",
        email: "Email address",
        property: "Property address or postcode",
        message: "Anything you'd like to add (optional)",
      },
      submit: "Request My Proposal",
      sending: "Sending…",
      success: "Thank you — your enquiry has been received. We will be in touch shortly.",
      error: "Something went wrong. Please try again, or call us on 020 3873 6816.",
    },
    cta: {
      overline: "Speak to our team",
      heading: "Predictable income. Professional management. Complete peace of mind.",
      paragraph: "If you would like dependable rental income without the day-to-day demands of being a landlord, we would be glad to talk. There is no obligation — just a clear, honest conversation about your property and your goals.",
      button: "Call Us",
      labels: { phone: "Telephone", email: "Email", address: "Registered Office" },
    },
    rentersFaq: {
      overline: "The Renters' Rights Bill",
      heading: "Company lets and the Renters' Rights Bill, explained",
      intro: "The rental landscape is changing. Here is what the reforms focus on — and why a professionally structured company let can offer landlords greater flexibility and certainty.",
      items: [
        {
          q: "How could the Renters' Rights Bill affect landlords?",
          a: "The Renters' Rights Bill — now enacted as the Renters' Rights Act — mainly reforms Assured Shorthold Tenancies (ASTs), the most common form of residential tenancy in England. Its measures include the end of 'no-fault' Section 21 evictions, a move towards periodic tenancies, and tighter rules on rent increases and grounds for possession. For landlords letting on standard ASTs, this can mean longer commitments and reduced flexibility when regaining possession. At Golden Comfort Property Management, we help landlords understand their position and protect their interests through professional management, regular inspections, full compliance and clear communication at every stage.",
        },
        {
          q: "Are company lets affected in the same way as residential tenancies?",
          a: "Generally, no. A genuine company let — where a limited company, rather than an individual, is the tenant — is a commercial agreement and is usually not an Assured Shorthold Tenancy. As a result, many of the residential tenancy rules introduced by the Bill do not apply in the same way. However, this depends on the arrangement being genuine, correctly structured and properly documented; every case turns on its own facts. We make sure our agreements are professionally drafted and compliant with current UK law, and we protect landlords' interests through careful management, regular inspections and transparent communication.",
        },
        {
          q: "Why do many landlords choose company lets?",
          a: "Company lets can offer greater contractual flexibility than a standard residential tenancy — for example, around notice arrangements, the length of the term and how possession is handled at the end of an agreement — depending on the circumstances and how the agreement is structured. They also give landlords a professional counterparty: a company with clear responsibilities under the agreement, rather than an individual tenant. With Golden Comfort, that structure comes together with guaranteed rent, professional management, regular inspections and clear communication, so your interests remain protected throughout the term.",
        },
        {
          q: "What are the benefits of letting to a company rather than an individual tenant?",
          a: "Predictability and simplicity. With a company let, your rent is agreed up front and paid by the company whether or not the property is occupied; the company manages the occupants, day-to-day upkeep and all tenant liaison; and the agreement is commercial in nature, which can allow more flexible terms. You deal with one professional, accountable organisation rather than individual occupiers. At Golden Comfort, we add regular inspections, compliance oversight and straightforward reporting, keeping both your property and your income well looked after.",
        },
        {
          q: "How does Golden Comfort Property Management help reduce landlord risk?",
          a: "We act as your professional tenant and managing partner under a correctly documented company let agreement. Your rent is guaranteed for the term; we manage occupants, maintenance and compliance requirements; we carry out regular inspections; and we keep you informed with clear, honest communication. No arrangement can remove risk entirely, but our model is designed to minimise voids, arrears and day-to-day involvement — with every agreement structured to comply with current UK law.",
        },
      ],
      disclaimer: "The information above is provided for general guidance only and does not constitute legal advice. How the legislation applies depends on individual circumstances and how each agreement is structured. We always recommend that landlords obtain independent legal advice where needed.",
    },
    compliance: {
      label: "Registered & accountable",
      items: [
        "Property Redress Scheme (PRS) — Member",
        "Information Commissioner's Office (ICO) — Registered",
      ],
    },
    footer: {
      brand: "Golden Comfort",
      tagline: "Property Management",
      rights: "All rights reserved.",
      company: "Golden Comfort Property Management",
    },
  },
  ar: {
    metaTitle: "جولدن كومفورت لإدارة العقارات | إيجار مضمون لملاك العقارات في لندن",
    langSwitchLabel: "English",
    langSwitchPath: "/",
    nav: [
      { label: "لماذا نحن", href: "#why-us", id: "nav-why-us" },
      { label: "كيف نعمل", href: "#how-it-works", id: "nav-how-it-works" },
      { label: "من نحن", href: "#about", id: "nav-about" },
      { label: "الأسئلة الشائعة", href: "#faq", id: "nav-faq" },
    ],
    getInTouch: "تواصل معنا",
    hero: {
      overline: "إيجار مضمون · إدارة شاملة · لندن",
      headline: "دخل إيجاري يمكنك الاعتماد عليه، دون أن ترفع إصبعاً.",
      sub: "نضمن لك إيجارك لمدة تصل إلى خمس سنوات ونتولى إدارة كل شيء — من المستأجرين إلى الصيانة وأدق التفاصيل — ليعمل عقارك لصالحك، لا العكس.",
      reassurance: "إيجار ثابت يُدفع في موعده كل شهر — مهما كانت ظروف السوق.",
      positioning: "شركة عقارية موثوقة وراسخة، تخدم الملاك في جميع أنحاء لندن",
      bgAlt: "أفق مدينة لندن عند الغروب",
    },
    trust: [
      "إيجار مضمون لمدة تصل إلى خمس سنوات",
      "لا فترات شغور — إطلاقاً",
      "لا رسوم تأسيس أو إدارة",
    ],
    value: {
      overline: "لماذا يختارنا الملاك",
      heading: "طريقة أبسط وأكثر أماناً لتأجير عقارك",
      intro: "كل جانب من جوانب خدمتنا مبني على ثلاثة مبادئ: تقليل المخاطر، وثبات الدخل، وتبسيط التجربة بأكملها.",
      imageAlt: "داخلية شقة لندنية مؤثثة باحتراف",
      items: [
        {
          title: "إيجار مضمون لمدة تصل إلى خمس سنوات",
          copy: "دفعة شهرية ثابتة، متفق عليها منذ البداية وتُدفع في موعدها طوال مدة العقد. لا فترات شغور، ولا متأخرات، ولا ملاحقة — يبقى دخلك ثابتاً مهما تغيرت ظروف السوق.",
        },
        {
          title: "تجديد وتأثيث مجاني",
          copy: "عند الحاجة، نقوم بتجديد عقارك وتأثيثه على نفقتنا — مما يزيد جاذبيته للمستأجرين ويحافظ على حالته على المدى الطويل، دون أي تكلفة عليك.",
        },
        {
          title: "إدارة شاملة للمستأجرين والصيانة",
          copy: "تحصيل الإيجار، والتواصل مع المستأجرين، والتفتيش، والصيانة اليومية — كلها من مسؤولية فريقنا. جهة اتصال واحدة، ودون أي تدخل منك.",
        },
        {
          title: "خيارات شراء اختيارية",
          copy: "في نهاية الاتفاقية، اختر ما يناسب أهدافك: التجديد، أو استعادة العقار خالياً من شاغليه، أو دراسة خيار شراء متفق عليه.",
        },
        {
          title: "خبرة محلية في لندن",
          copy: "نستقطب مستأجرين محترفين ذوي جودة عالية عبر كبرى جهات العمل والشبكات المهنية في لندن — مستأجرون موثوقون، تم التحقق منهم بعناية.",
        },
      ],
    },
    process: {
      overline: "كيف نعمل",
      heading: "من أول استفسار إلى دخل مُدار بالكامل",
      intro: "رحلة واضحة من ست خطوات صُممت حول اليقين. ستعرف دائماً ما الخطوة التالية، ومن المسؤول، ومتى يُدفع إيجارك.",
      imageAlt: "منزل لندني تقليدي",
      steps: [
        {
          title: "الاستشارة الأولية وتقييم العقار",
          copy: "نزور عقارك، ونراجع حالته وموقعه الإيجاري، ونجيب عن أسئلتك بعبارات واضحة ومباشرة.",
        },
        {
          title: "هيكلة الاتفاقية وإعداد الإيجار المضمون",
          copy: "نتفق على إيجار شهري ثابت ومدة تصل إلى خمس سنوات، موضحة بوضوح في اتفاقية إدارة بسيطة وشفافة.",
        },
        {
          title: "التجديد والتأثيث",
          copy: "عند الحاجة، ننجز التحسينات التجميلية والتأثيث على نفقتنا، لنجهز عقارك بمعايير احترافية.",
        },
        {
          title: "اختيار المستأجرين المحترفين",
          copy: "نضع مستأجرين محترفين تم التحقق منهم ومراجعتهم، عبر شبكاتنا من جهات العمل وشركات إعادة التوطين في لندن.",
        },
        {
          title: "الإدارة المستمرة والصيانة والتقارير",
          copy: "نتولى تحصيل الإيجار والصيانة والتفتيش والتواصل مع المستأجرين — مع إطلاعك على تحديثات واضحة ومنتظمة.",
        },
        {
          title: "خيارات نهاية المدة",
          copy: "عند انتهاء المدة، الخيار لك: تجديد الاتفاقية، أو استعادة العقار خالياً من شاغليه، أو مناقشة شراء متفق عليه.",
        },
      ],
    },
    authority: {
      overline: "خبرة يمكنك الاعتماد عليها",
      heading: "معرفة محلية. رؤية مُثبتة.",
      intro: "تأسست جولدن كومفورت لإدارة العقارات على سنوات من الخبرة العملية في قلب سوق العقارات اللندني — من مبيعات العقارات الفاخرة في وسط لندن مع اثنتين من أعرق شركات الوساطة في العاصمة، إلى تقديم المشورة للمستثمرين الدوليين حول مشاريع البيع على الخارطة. نطبق هذه الخبرة لحماية دخلك وعقارك وراحة بالك.",
      photoAlt: "هيدر الحكيم، مؤسس جولدن كومفورت لإدارة العقارات",
      founderName: "هيدر الحكيم — المؤسس",
      founderStory: "بنى هيدر الحكيم مسيرته المهنية في قلب سوق العقارات في لندن، مطوراً فهماً عميقاً لما يصنع القيمة والفرص والعلاقات الدائمة. وبخبرة تمتد عبر مبيعات العقارات السكنية الفاخرة والمشاريع الجديدة والاستثمار الدولي، ساهم في إتمام صفقات عقارية بملايين الجنيهات. واليوم، يوظف تلك الرؤية التجارية ومهارات التفاوض الواثقة ونهج العميل أولاً في مشواره العقاري الخاص، جامعاً بين المعرفة المحلية والرؤية الدولية لتقديم حلول عقارية موثوقة ومُدارة باحتراف في جميع أنحاء لندن.",
      careerLine: "مسيرة مهنية عبر Kinleigh Folkard & Hayward وDexters وGFS Developments — من مبيعات العقارات الفاخرة في وسط لندن إلى الاستثمار الدولي في مشاريع البيع على الخارطة.",
      credentials: [
        "مبيعات العقارات الفاخرة في وسط لندن",
        "الاستثمار في مشاريع البيع على الخارطة",
        "علاقات المستثمرين — بالإنجليزية والعربية",
      ],
      stats: [
        { value: "+£18m", label: "صفقات عقارية عبر القطاع السكني والبيع على الخارطة" },
        { value: "£6.8m", label: "صفقات بيع مكتملة في عام واحد في العقارات الفاخرة بلندن" },
        { value: "+30", label: "عقاراً فاخراً في وسط لندن تحت الإدارة في وقت واحد" },
        { value: "+50", label: "مستثمراً دولياً في المملكة المتحدة والإمارات والسعودية" },
      ],
    },
    faq: {
      overline: "الأسئلة الشائعة",
      heading: "إجابات واضحة، قبل أن تلتزم",
      items: [
        {
          q: "كيف يعمل الإيجار المضمون؟",
          a: "نتفق معك على دفعة إيجار شهرية ثابتة في بداية العقد، وندفع هذا المبلغ بانتظام طوال مدة الاتفاقية. لا يتأثر دخلك بمستوى إشغال المستأجرين، مما يمنحك عوائد يمكن التنبؤ بها والاعتماد عليها.",
        },
        {
          q: "ماذا يحدث إذا كان العقار شاغراً؟",
          a: "تستمر في استلام إيجارك المتفق عليه كاملاً. فترات الشغور مسؤوليتنا نحن، لا مسؤوليتك. نموذج عملنا مبني على استقطاب المستأجرين المحترفين وإدارة قوية لمعدلات الإشغال.",
        },
        {
          q: "من يتحمل تكاليف الصيانة؟",
          a: "الصيانة اليومية والمشكلات المتعلقة بالمستأجرين يديرها فريقنا وينسقها بالكامل. نتولى التواصل وإدارة المقاولين والإشراف. أما الأعمال الإنشائية الكبرى فتبقى من مسؤولية المالك ما لم يُتفق كتابياً على غير ذلك.",
        },
        {
          q: "كيف يتم استقطاب المستأجرين والتحقق منهم؟",
          a: "نعمل مع جهات عمل محلية راسخة وشبكات مهنية لاستقطاب مستأجرين ذوي جودة عالية. يتم التحقق من جميع المقيمين ومراجعتهم وفق متطلبات الامتثال وأفضل المعايير المهنية.",
        },
        {
          q: "ماذا يحدث في نهاية الاتفاقية؟",
          a: "لديك خيارات واضحة: يمكنك تجديد الاتفاقية، أو استعادة العقار خالياً من شاغليه، أو دراسة مسار شراء متفق عليه إذا كان ذلك يتوافق مع أهدافك طويلة المدى.",
        },
        {
          q: "هل يمكنني البيع أثناء مدة العقد؟",
          a: "نعم. يمكننا دعم عملية البيع وفق شروط اتفاقية الإدارة. وفي بعض الحالات، قد نقدم أيضاً حلاً للشراء المباشر إذا كان ذلك يناسب أهدافك.",
        },
        {
          q: "هل توجد رسوم مقدمة؟",
          a: "لا. لا نفرض رسوم تأسيس أو إدارة لإدراج عقارك في برنامج الإيجار المضمون. نموذجنا مصمم ليكون واضحاً ومتوافقاً مع شراكة طويلة المدى.",
        },
      ],
    },
    enquiry: {
      overline: "اطلب معاودة الاتصال",
      heading: "حدثنا عن عقارك",
      intro: "شاركنا بعض التفاصيل وسنعاود الاتصال بك لمناقشة عرض إيجار مضمون لعقارك. دون أي التزام أو رسوم — مجرد رقم واضح ومحادثة مباشرة.",
      points: [
        "عرض إيجار مضمون لعقارك",
        "شرح واضح لآلية عمل الاتفاقية",
        "دون أي التزام أو رسوم، إطلاقاً",
      ],
      fields: {
        name: "الاسم الكامل",
        phone: "رقم الهاتف",
        email: "البريد الإلكتروني",
        property: "عنوان العقار أو الرمز البريدي",
        message: "أي تفاصيل إضافية (اختياري)",
      },
      submit: "اطلب عرضك",
      sending: "جارٍ الإرسال…",
      success: "شكراً لك — تم استلام استفسارك. سنتواصل معك قريباً.",
      error: "حدث خطأ ما. يرجى المحاولة مرة أخرى، أو الاتصال بنا على 020 3873 6816.",
    },
    cta: {
      overline: "تحدث إلى فريقنا",
      heading: "دخل يمكن التنبؤ به. إدارة احترافية. وراحة بال تامة.",
      paragraph: "إذا كنت ترغب في دخل إيجاري ثابت دون أعباء الإدارة اليومية، يسعدنا التحدث إليك. لا يوجد أي التزام — مجرد محادثة واضحة وصادقة حول عقارك وأهدافك.",
      button: "اتصل بنا",
      labels: { phone: "الهاتف", email: "البريد الإلكتروني", address: "المكتب المسجل" },
    },
    rentersFaq: {
      overline: "مشروع قانون حقوق المستأجرين",
      heading: "عقود إيجار الشركات ومشروع قانون حقوق المستأجرين، ببساطة",
      intro: "مشهد قطاع الإيجار يتغير. إليك ما تركز عليه الإصلاحات — ولماذا يمكن أن يوفر عقد إيجار شركة منظم باحتراف مرونة ويقيناً أكبر للملاك.",
      items: [
        {
          q: "كيف يمكن أن يؤثر مشروع قانون حقوق المستأجرين على الملاك؟",
          a: "يركز مشروع قانون حقوق المستأجرين بشكل أساسي على إصلاح عقود الإيجار السكنية قصيرة الأجل المضمونة (AST)، وهي الصيغة الأكثر شيوعاً لعقود الإيجار السكنية في إنجلترا. وتشمل تدابيره إنهاء إجراءات الإخلاء «دون إبداء أسباب» بموجب المادة 21، والتحول نحو عقود الإيجار الدورية، وقواعد أكثر صرامة بشأن زيادات الإيجار وأسباب استرداد العقار. وبالنسبة للملاك الذين يؤجرون بعقود AST تقليدية، فقد يعني ذلك التزامات أطول أمداً ومرونة أقل عند استرداد العقار. في جولدن كومفورت لإدارة العقارات، نساعد الملاك على فهم وضعهم ونحمي مصالحهم من خلال إدارة احترافية وتفتيش دوري والتزام كامل بالأنظمة وتواصل واضح في كل مرحلة.",
        },
        {
          q: "هل تتأثر عقود إيجار الشركات بنفس طريقة عقود الإيجار السكنية؟",
          a: "بشكل عام، لا. فعقد إيجار الشركة الحقيقي — حيث تكون شركة محدودة هي المستأجر وليس فرداً — يُعد اتفاقية تجارية ولا يكون عادةً من عقود الإيجار السكنية قصيرة الأجل المضمونة. ونتيجة لذلك، لا تنطبق العديد من قواعد الإيجار السكني التي يقدمها مشروع القانون بنفس الطريقة. ومع ذلك، يعتمد ذلك على كون الترتيب حقيقياً ومنظماً بشكل صحيح وموثقاً كما ينبغي؛ فكل حالة تُقيَّم وفق ظروفها الخاصة. نحرص على أن تكون اتفاقياتنا معدة باحتراف ومتوافقة مع القانون البريطاني المعمول به، ونحمي مصالح الملاك من خلال إدارة دقيقة وتفتيش منتظم وتواصل شفاف.",
        },
        {
          q: "لماذا يختار العديد من الملاك عقود إيجار الشركات؟",
          a: "يمكن أن توفر عقود إيجار الشركات مرونة تعاقدية أكبر مقارنة بعقد الإيجار السكني التقليدي — على سبيل المثال، في ترتيبات الإشعار ومدة العقد وكيفية استرداد العقار في نهاية الاتفاقية — وذلك بحسب الظروف وطريقة هيكلة الاتفاقية. كما تمنح الملاك طرفاً تعاقدياً مهنياً: شركة تتحمل مسؤوليات واضحة بموجب الاتفاقية، بدلاً من مستأجر فرد. ومع جولدن كومفورت، يجتمع هذا الهيكل مع إيجار مضمون وإدارة احترافية وتفتيش دوري وتواصل واضح، لتبقى مصالحك محمية طوال مدة العقد.",
        },
        {
          q: "ما هي مزايا التأجير لشركة بدلاً من مستأجر فرد؟",
          a: "الوضوح والبساطة. فمع عقد إيجار الشركة، يُتفق على إيجارك مسبقاً وتدفعه الشركة سواء كان العقار مشغولاً أم لا؛ وتتولى الشركة إدارة شاغلي العقار والصيانة اليومية وجميع أمور التواصل معهم؛ وتكون الاتفاقية تجارية بطبيعتها، مما قد يسمح بشروط أكثر مرونة. أنت تتعامل مع جهة مهنية واحدة خاضعة للمساءلة بدلاً من أفراد. وفي جولدن كومفورت، نضيف إلى ذلك التفتيش المنتظم والإشراف على الامتثال وتقارير واضحة، للحفاظ على عقارك ودخلك في أيدٍ أمينة.",
        },
        {
          q: "كيف تساعد جولدن كومفورت لإدارة العقارات في تقليل مخاطر الملاك؟",
          a: "نعمل كمستأجر مهني وشريك إداري لك بموجب اتفاقية إيجار شركة موثقة بشكل صحيح. إيجارك مضمون طوال مدة العقد؛ ونتولى إدارة شاغلي العقار والصيانة ومتطلبات الامتثال؛ ونجري تفتيشات دورية؛ ونبقيك على اطلاع عبر تواصل واضح وصادق. وعلى الرغم من أنه لا يمكن لأي ترتيب أن يلغي المخاطر تماماً، فإن نموذجنا مصمم لتقليل فترات الشغور والمتأخرات والتدخل اليومي إلى أدنى حد — مع هيكلة كل اتفاقية بما يتوافق مع القانون البريطاني المعمول به.",
        },
      ],
      disclaimer: "المعلومات الواردة أعلاه لأغراض الإرشاد العام فقط ولا تُعد استشارة قانونية. يعتمد تطبيق التشريع على الظروف الفردية وطريقة هيكلة كل اتفاقية. نوصي الملاك دائماً بطلب مشورة قانونية مستقلة عند الحاجة.",
    },
    compliance: {
      label: "مسجّلون وخاضعون للرقابة",
      items: [
        "أعضاء في برنامج إصلاح النزاعات العقارية (PRS)",
        "مسجلون لدى مكتب مفوض المعلومات (ICO)",
      ],
    },
    footer: {
      brand: "جولدن كومفورت",
      tagline: "لإدارة العقارات",
      rights: "جميع الحقوق محفوظة.",
      company: "جولدن كومفورت لإدارة العقارات",
    },
  },
};
